import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useDoorlyProAuth } from '../context/DoorlyProAuthContext';
import { useIsMobile } from '../utils/useIsMobile';
import { Icon } from '../utils/icons';
import DoorlyChat, { OPEN_EVA_EVENT } from './DoorlyChat';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const SIDEBAR_WIDTH = 240;
const TOPBAR_HEIGHT = 56;
const PRIMARY = '#3a6ad9';

const NAV_ITEMS = [
  {
    label: 'Dashboard',
    path: '/doorlypro/dashboard',
    mobileIcon: 'home',
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
    ),
  },
  {
    // 🚨 THE REFERRAL HALF OF THE PRODUCT MAPPING, WHICH WENT UNENFORCED FOR A
    // DAY. "Submit an Application" was correctly hidden from realtors while both
    // buyer pages stayed visible to loan officers — half a mapping, which reads
    // as a rule and leaves the opposite door open.
    //
    // ⚠️ AND THIS HALF HAS A PAYMENT BEHIND IT. An LO who filed a referral would
    // be owed 1% of the LOAN AMOUNT by referralCommission — the origination rate,
    // for an introduction with no origination in it. See referralRefusalForType.
    label: 'My Buyers',
    path: '/doorlypro/referrals',
    mobileIcon: 'users',
    showInMobileNav: true,
    hideForLoanOfficer: true,
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
  },
  {
    label: 'New Buyer',
    path: '/doorlypro/referrals/new',
    mobileIcon: 'plus',
    hideInMobileNav: true,
    hideForLoanOfficer: true,
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" />
      </svg>
    ),
  },
  {
    // 🚨 GATED ON THE ACCOUNT'S CAPABILITY, AND HIDDEN RATHER THAN DISABLED. The
    // rule in this codebase is that out-of-scope reads as not-there — admin
    // returns 404 rather than 403 for the same reason. A greyed-out "Submit an
    // Application" tells every realtor a wholesale channel exists and they are
    // not in it, which is a product announcement nobody decided to make.
    //
    // ⚠️ HIDDEN FROM BROWSING, EXPLAINED IF YOU ARRIVE. The page itself renders
    // the capability refusal — which already says what is missing and what staff
    // would do about it — so a broker sent a direct link is not stonewalled.
    label: 'Submit an Application',
    path: '/doorlypro/submissions',
    mobileIcon: 'upload',
    requiresSubmitCapability: true,
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    ),
  },
  {
    // ── 🚨 AN ORIGINATOR'S OWN PIPELINE, WHICH HAD NO PAGE ──────────────────
    //
    // A realtor has My Buyers reading buyer_referrals; an originator's files
    // live in broker_submissions and appeared only at the bottom of the
    // submission form. So an LO could see what they had SENT and not what had
    // become of it.
    //
    // ⚠️ hideForRealtor, THE MIRROR OF My Buyers' hideForLoanOfficer. The
    // comment on that entry warns that half a mapping "reads as a rule and
    // leaves the opposite door open" — this is the other half.
    //
    // 🔑 DIRECTLY UNDER Submit an Application, because that is where a file
    // comes from and this is where it goes.
    label: 'My Borrowers',
    path: '/doorlypro/borrowers',
    mobileIcon: 'users',
    showInMobileNav: true,
    hideForRealtor: true,
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
  },
  // 🚨 FIRM SETTINGS REMOVED — IT WAS A SECOND DOOR TO ONE ROOM. The page held
  // the firm's payout banking and the firm's W-9. Banking already lives on the
  // profile, behind the same OTP re-auth and the same accountBankingAuthority
  // check, and that is where the banking TASK has always pointed; the W-9 is now
  // an option in the Documents upload list for an account-level holder, posting
  // to the same /firm/w9 endpoint. Two routes to one job is how a partner ends up
  // unsure which is the real one — and which of the two a fix has to land in.
  {
    label: 'Your Team',
    path: '/doorlypro/firm/team',
    mobileIcon: 'users',
    requiresBroker: true,
    hideInMobileNav: true,
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
  },
  {
    // 🚨 THE FIRM'S PIPELINE — PROGRESS ACROSS EVERY AGENT, NEVER CONTENTS. The
    // same rule that governs an agent's own view governs this one; what widens is
    // WHOSE files, not WHAT is shown of them. Notably a broker sees no shopping
    // range at all: that consent is recorded per agent, and it was not given to
    // them. See utils/firmRollup.js.
    label: 'Team Pipeline',
    path: '/doorlypro/firm/pipeline',
    mobileIcon: 'users',
    requiresBroker: true,
    hideInMobileNav: true,
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
  },
  {
    label: 'Check Eligibility',
    path: '/doorlypro/eligibility',
    mobileIcon: 'search',
    hideInMobileNav: true,
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    ),
  },
  {
    label: 'Compensation',
    path: '/doorlypro/compensation',
    mobileIcon: 'currency',
    hideForHomebuilder: true,
    hideInMobileNav: true,
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
      </svg>
    ),
  },
  {
    label: 'Documents',
    path: '/doorlypro/documents',
    mobileIcon: 'document',
    hideInMobileNav: true,
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    label: 'Messages',
    path: '/doorlypro/messages',
    mobileIcon: 'chat',
    showInMobileNav: true,
    section: 'bottom',
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
    ),
  },
  {
    label: 'Alerts',
    path: '/doorlypro/alerts',
    mobileIcon: 'bell',
    hideInMobileNav: true,
    section: 'bottom',
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 01-3.46 0" />
      </svg>
    ),
  },
  {
    label: 'My Profile',
    path: '/doorlypro/profile',
    mobileIcon: 'user',
    showInMobileNav: true,
    section: 'profile',
    icon: (
      <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
      </svg>
    ),
  },
];

export function RequirePartnerAuth({ children }) {
  const { partner, partnerLoading } = useDoorlyProAuth();
  const location = useLocation();
  if (partnerLoading) return null;
  // 🔑 WITH THE RETURN PATH (Hannah, 2026-11-02 — Onboarding and Agreement wrapped too): DoorlyProLogin honours ?next=
  // (utils/safeNextPath), not router state, so a signed-out partner who opened a page from an email lands back on it.
  if (!partner) {
    const next = `${location.pathname}${location.search || ''}`;
    return <Navigate to={`/doorlypro/login?next=${encodeURIComponent(next)}`} state={{ from: location.pathname }} replace />;
  }
  return children;
}

export default function DoorlyProLayout({ children }) {
  const { partner, partnerLogout } = useDoorlyProAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [unreadAlerts, setUnreadAlerts] = React.useState(0);
  // ⚠️ EVA MOVES TO THE LAYOUT. She was mounted only on DoorlyProDashboard, so
  // the Help control in this sidebar — which appears on EVERY partner page —
  // would have dispatched an open event that nothing was listening for anywhere
  // except the dashboard. The borrower app mounts her in AppShell for exactly
  // this reason; DoorlyPro now matches.
  const partnerToken = partner ? (localStorage.getItem('doorlypro_token') || localStorage.getItem('partner_token')) : null;
  useEffect(() => { document.title = 'DoorlyPro | Partner Portal'; }, []);

  // Poll unread alert count
  useEffect(() => {
    const tok = partner ? (localStorage.getItem('doorlypro_token') || localStorage.getItem('partner_token')) : null;
    if (!tok) return;
    const fetch_ = () => {
      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/doorlypro/alerts/unread-count`, { headers: { Authorization: `Bearer ${tok}` } })
        .then(r => r.json()).then(d => setUnreadAlerts(d.unread_count || 0)).catch(() => {});
    };
    fetch_();
    const i = setInterval(fetch_, 30000);
    return () => clearInterval(i);
  }, [partner]);

  const isHomebuilder = partner?.partner_type === 'homebuilder';
  // ⚠️ READ FROM THE TOKEN FOR DISPLAY ONLY, exactly like isHomebuilder above.
  // The server refuses the referral route independently (referralRefusalForType,
  // read from the DATABASE rather than the JWT), so a stale token can hide or
  // show a menu item but can never decide whether a referral is accepted.
  const isLoanOfficer = partner?.partner_type === 'loan_officer';
  // 🚨 EXPLICIT, NOT "NOT A LOAN OFFICER". An unset or unrecognised partner type
  // must not be treated as a realtor — it would show them a pipeline page for a
  // product nobody has decided they have. Same discipline as partnerTasks
  // refusing to guess a type.
  const isRealtor = partner?.partner_type === 'realtor';
  const partnerName = partner
    ? [partner.first_name, partner.last_name].filter(Boolean).join(' ') || partner.email || 'Partner'
    : 'Partner';
  // ⚠️ SETTLED BY THE SERVER, SEEDED FROM THE TOKEN FOR THE FIRST PAINT ONLY.
  // partner_type and account_id are baked into the JWT when it is issued, so a
  // partner whose record staff corrected an hour ago carries a stale type until
  // they log back in — the schema endpoint is the answer, and it overwrites this
  // either way. The seed exists purely so the first frame is not uniformly
  // wrong.
  //
  // 🚨 IT STARTED AT false AND THAT IS WHAT MADE THE SIDEBAR JUMP. Every refresh
  // rendered the nav without this item, then inserted it a few hundred ms later
  // when the fetch landed, shoving Check Eligibility, Compensation, Documents
  // and everything below it down a row. A loading state you can SEE is a bug
  // when the answer was already sitting in the token: `partner` is the decoded
  // JWT and is populated before this layout mounts (RequirePartnerAuth holds the
  // render until partnerLoading clears), which is why the homebuilder rule three
  // lines up can read partner_type synchronously and this can too.
  //
  // ⚠️ THE SEED MIRRORS THE SERVER'S PRODUCT ROUTING, NOT AN APPROVAL RULE —
  // constants/partnerTypes.js PRODUCT_FOR_TYPE is the source, and loan_officer is
  // the type that sends loan files. If a type is ever added there, this needs the
  // same edit; being wrong here costs one frame, not access, because the server
  // still decides and the page itself refuses independently.
  // 🚨 THE NAV GREW A ROW ON EVERY PAGE LOAD, AND THIS IS WHY. Both role answers
  // below start pessimistic and are corrected by a fetch, so the sidebar painted
  // a short list and then expanded under the cursor — on every navigation, since
  // the layout remounts. The answers cannot be seeded (see the note on isBroker:
  // a 24-hour token would keep showing a removed broker their firm's borrowers),
  // so the list waits instead of guessing.
  //
  // ⚠️ SETTLED, NOT TRUE. Both flags flip on failure as well as success — a
  // failed check is a settled answer of "no", and the nav must not hang on it.
  const [rolesSettled, setRolesSettled] = useState({ submit: false, broker: false });
  const navReady = rolesSettled.submit && rolesSettled.broker;

  // ── 🚨 TWO STATES, NOT ONE BOOLEAN ──────────────────────────────────────
  //
  // canSubmit was "may this partner post a loan file", and it drove BOTH the
  // nav and the page. That conflated two answers a partner needs to hear
  // differently:
  //
  //   NOT YOUR PRODUCT — a realtor. Permanent. Hidden, per the note on the item
  //     below: a greyed-out entry announces a channel they can never enter.
  //   NOT YET — a loan officer who has not signed. One signature away. HIDING it
  //     tells them the channel does not exist, when it is a minute's work; it
  //     also means they can never REACH the page that explains how to fix it.
  //
  // 🔑 DERIVED FROM THE SERVER'S OWN REFUSAL CODE, never re-decided here. The
  // schema endpoint returns submissionRefusal()'s reason verbatim; the frontend
  // reads which reason it was. A second frontend check on agreement_signed is
  // how the page and the endpoint come to disagree.
  const [canSubmit, setCanSubmit] = useState(() => partner?.partner_type === 'loan_officer');
  const [submitPending, setSubmitPending] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('doorlypro_token');
    // ⚠️ NO TOKEN IS ALSO A SETTLED ANSWER. Without this the skeleton would be
    // permanent for a signed-out render of the shell.
    if (!token) { setRolesSettled((p) => ({ ...p, submit: true })); return; }
    let live = true;
    fetch(`${API_URL}/api/doorlypro/submissions/schema`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!live) return;
        setCanSubmit(!!(d && d.allowed));
        // ⚠️ BOTH SETUP REFUSALS, NOT JUST THE AGREEMENT. "Pending" means the
        // partner reaches the page and is shown what to finish; the nav read
        // only the agreement code, so an unsigned partner who also owed
        // training stopped looking pending the moment the server started
        // answering PARTNER_NOT_ACTIVE instead — and the item would have
        // hidden rather than inviting them in to read the list.
        setSubmitPending(d?.refusal?.error === 'AGREEMENT_NOT_EXECUTED'
          || d?.refusal?.error === 'PARTNER_NOT_ACTIVE');
        setRolesSettled((p) => ({ ...p, submit: true }));
      })
      // ⚠️ FAILS CLOSED. An unreachable check hides the item rather than showing
      // a broker a page that will refuse them.
      // ⚠️ FAILS CLOSED ON BOTH. An unreachable check hides the item rather than
      // showing a broker a page that will refuse them, and it does NOT guess
      // that the reason was a missing signature.
      .catch(() => { if (live) { setCanSubmit(false); setSubmitPending(false); setRolesSettled((p) => ({ ...p, submit: true })); } });
    return () => { live = false; };
  }, []);

  // ── firm-wide sight, asked of the server on every mount ──────────────────
  //
  // 🚨 NO SEED FROM THE TOKEN, UNLIKE canSubmit ABOVE. That one seeds from
  // partner_type because being wrong costs one frame of a nav item whose page
  // refuses independently. This gates a view of OTHER PEOPLE'S BORROWERS, and a
  // token lasts 24 hours — so a removed broker seeded from it would see the firm
  // item until they logged out. Starts false and stays false until the server
  // says otherwise.
  // 🔑 AGREEMENTS WAITING ON THIS BROKER, ON THE DOCUMENTS ITEM. The queue lives
  // on that page and the nav said nothing about it, so a broker with no other
  // reason to open Documents would never learn their team's fees were held.
  //
  // ⚠️ READ FROM THE TASK LIST, NOT COUNTED HERE. utils/partnerTasks.js already
  // computes it and the dashboard already renders it — a second count in the nav
  // is how a badge and the row it points at start disagreeing.
  const [countersignCount, setCountersignCount] = useState(0);
  useEffect(() => {
    const token = localStorage.getItem('doorlypro_token');
    if (!token) return;
    let live = true;
    fetch(`${API_URL}/api/doorlypro/onboarding`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!live) return;
        const t = (d?.task_state?.tasks || []).find((x) => x.key === 'countersign_queue');
        setCountersignCount(t && !t.done ? (t.count || 0) : 0);
      })
      .catch(() => { if (live) setCountersignCount(0); });
    return () => { live = false; };
  }, [location.pathname]);

  const [isBroker, setIsBroker] = useState(false);
  useEffect(() => {
    const token = localStorage.getItem('doorlypro_token');
    if (!token) { setRolesSettled((p) => ({ ...p, broker: true })); return; }
    let live = true;
    fetch(`${API_URL}/api/doorlypro/firm/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (live) { setIsBroker(!!(d && d.isBroker)); setRolesSettled((p) => ({ ...p, broker: true })); } })
      // ⚠️ FAILS CLOSED, same as the capability check.
      .catch(() => { if (live) { setIsBroker(false); setRolesSettled((p) => ({ ...p, broker: true })); } });
    return () => { live = false; };
  }, []);

  // 🔑 ONE BADGE RENDERER, CALLED BY EVERY NAV LIST (broker report 1102, Jesse 2026-11-02). The Documents countersign
  // badge was written inside the BOTTOM-section loop only, and Documents is a MAIN item (no section) — so the badge
  // could never render: the item it names is drawn by a loop that had no badge. The main list, the bottom list and the
  // mobile drawer all call this now, so a badge shows wherever its item is drawn.
  const navBadge = (item) => {
    const n = item.label === 'Documents' ? countersignCount : item.label === 'Alerts' ? unreadAlerts : 0;
    if (!(n > 0)) return null;
    return (
      <span data-testid={`nav-badge-${item.label.toLowerCase()}`}
        title={item.label === 'Documents' ? `${n} agreement${n === 1 ? '' : 's'} waiting for your signature` : undefined}
        style={{ background: '#ff082f', color: '#fff', fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center' }}>{n > 99 ? '99+' : n}</span>
    );
  };

  const visibleNav = NAV_ITEMS
    .filter(item => !(item.hideForHomebuilder && isHomebuilder))
    .filter(item => !(item.hideForLoanOfficer && isLoanOfficer))
    // ⚠️ THE MIRROR PREDICATE. My Buyers carried hideForLoanOfficer with a
    // comment warning that half a mapping "reads as a rule and leaves the
    // opposite door open"; My Borrowers is the other half, and without this
    // filter a realtor would see an originator's pipeline page in their nav.
    .filter(item => !(item.hideForRealtor && isRealtor))
    // 🔑 VISIBLE WHEN THE ONLY THING MISSING IS THEIR OWN SIGNATURE. See the
    // two-state note above — hiding it from an unsigned loan officer would put
    // the explanation behind the door it is explaining.
    .filter(item => !(item.requiresSubmitCapability && !canSubmit && !submitPending))
    // ⚠️ A FOURTH PREDICATE IN THE SAME CHAIN, NOT A NEW STRUCTURE. The flat list
    // with declarative flags already carries three of these; an enterprise role is
    // the same shape. HIDDEN rather than disabled, for the reason written above
    // "Submit an Application": a greyed-out Team item tells every agent an
    // enterprise tier exists and that they are not in it.
    .filter(item => !(item.requiresBroker && !isBroker))
    ;



  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'inherit', overflowX: 'hidden', maxWidth: '100vw', width: '100%' }}>

      {/* ── Desktop Sidebar ── */}
      {!isMobile && (
      <aside style={{
        width: SIDEBAR_WIDTH, minHeight: '100vh', backgroundColor: '#ffffff',
        borderRight: '1px solid #e5e7eb', position: 'fixed', top: 0, left: 0, bottom: 0,
        display: 'flex', flexDirection: 'column', zIndex: 100,
      }}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
          <Link to="/doorlypro/dashboard" style={{ display: 'inline-block' }}>
            {/* 🔑 Logo ~19% larger, proportions kept (Hannah's review 2026-11-03 item 5): 32 → 38px tall. */}
            <img src={`${process.env.PUBLIC_URL}/assets/DoorlyPro.png`} alt="DoorlyPro" data-testid="pro-logo-sidebar"
              style={{ height: 38, width: 'auto' }}
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
            <span style={{ display: 'none', fontSize: 18, fontWeight: 800, color: PRIMARY, letterSpacing: '-0.5px' }}>
              Doorly<span style={{ color: '#111827' }}>Pro</span>
            </span>
          </Link>
        </div>
        <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {/* Main nav items */}
          {/* ⚠️ PLACEHOLDER ROWS UNTIL THE ROLE ANSWERS LAND, so the list arrives at
              its final length once instead of growing. Same shape as the real rows
              — icon box plus label — so nothing shifts when they swap in. */}
          <div style={{ flex: 1 }}>
            {!navReady && [...Array(7)].map((_, i) => (
              <div key={`sk${i}`} aria-hidden="true"
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', marginBottom: 2 }}>
                <div style={{ width: 18, height: 18, borderRadius: 5, background: '#eef1f6' }} />
                <div style={{ height: 10, width: `${55 + (i % 3) * 12}%`, borderRadius: 5, background: '#eef1f6' }} />
              </div>
            ))}
            {navReady && visibleNav.filter(i => !i.section).map((item) => {
              const activeCheck = item.path === '/doorlypro/referrals'
                ? location.pathname === '/doorlypro/referrals'
                : location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                    borderRadius: 8, marginBottom: 2, textDecoration: 'none',
                    fontSize: 13.5, fontWeight: activeCheck ? 600 : 400,
                    color: activeCheck ? PRIMARY : '#374151',
                    backgroundColor: activeCheck ? '#eff3fe' : 'transparent',
                    transition: 'background-color 0.15s, color 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!activeCheck) { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.color = '#111827'; } }}
                  onMouseLeave={(e) => { if (!activeCheck) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#374151'; } }}
                >
                  <span style={{ color: activeCheck ? PRIMARY : '#6b7280', flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {navBadge(item)}
                </Link>
              );
            })}
          </div>

          {/* Divider + Messages & Alerts */}
          <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '8px', paddingTop: '8px' }}>
            {visibleNav.filter(i => i.section === 'bottom').map((item) => {
              const activeCheck = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                    borderRadius: 8, marginBottom: 2, textDecoration: 'none',
                    fontSize: 13.5, fontWeight: activeCheck ? 600 : 400,
                    color: activeCheck ? PRIMARY : '#374151',
                    backgroundColor: activeCheck ? '#eff3fe' : 'transparent',
                    transition: 'background-color 0.15s, color 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!activeCheck) { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.color = '#111827'; } }}
                  onMouseLeave={(e) => { if (!activeCheck) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#374151'; } }}
                >
                  <span style={{ color: activeCheck ? PRIMARY : '#6b7280', flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {navBadge(item)}
                </Link>
              );
            })}
          </div>

          {/* ── Help + Help centre ──
              Mirrors the borrower app's AppShell: a Help entry with an indented
              secondary link to the help centre beneath it.

              ⚠️ POINTS AT /partners, not the root. The partner collection is the
              only part of the help centre written for this audience; sending a
              partner to the borrower landing page is a worse answer than not
              linking at all.

              ⚠️ Doorly has no partner-side Eva, so Help routes to Messages —
              the partner team — rather than opening an assistant that does not
              exist here. The borrower app's Help opens Eva; copying that shape
              without the assistant behind it would be a dead control. */}
          <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '8px', paddingTop: '8px' }}>
            {(() => {
              return (
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); window.dispatchEvent(new Event(OPEN_EVA_EVENT)); }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                    borderRadius: 8, border: 'none', textAlign: 'left', cursor: 'pointer',
                    fontSize: 13.5, fontWeight: 400, fontFamily: 'inherit',
                    color: '#374151', backgroundColor: 'transparent',
                    transition: 'background-color 0.15s, color 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.color = '#111827'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#374151'; }}
                >
                  <span style={{ color: '#6b7280', flexShrink: 0 }}>
                    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" />
                    </svg>
                  </span>
                  <span style={{ flex: 1 }}>Help</span>
                </button>
              );
            })()}
            <a href="https://help.godoorly.com/partners" target="_blank" rel="noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px 5px 40px',
                borderRadius: 8, textDecoration: 'none', fontSize: 12, fontWeight: 500, color: '#9ca3af',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#6b7280'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; }}
            >
              Help center
              <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

          {/* My Profile — right above footer */}
          {visibleNav.filter(i => i.section === 'profile').map((item) => {
            const activeCheck = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                  borderRadius: 8, marginBottom: 0, textDecoration: 'none',
                  fontSize: 13.5, fontWeight: activeCheck ? 600 : 400,
                  color: activeCheck ? PRIMARY : '#374151',
                  backgroundColor: activeCheck ? '#eff3fe' : 'transparent',
                  transition: 'background-color 0.15s, color 0.15s',
                }}
                onMouseEnter={(e) => { if (!activeCheck) { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.color = '#111827'; } }}
                onMouseLeave={(e) => { if (!activeCheck) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#374151'; } }}
              >
                <span style={{ color: activeCheck ? PRIMARY : '#6b7280', flexShrink: 0 }}>{item.icon}</span>
                <span style={{ flex: 1 }}>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6', flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#3a6ad9', marginBottom: 6 }}>DoorlyPro Partner Portal</div>
          <div style={{ fontSize: 10, color: '#9ca3af', lineHeight: 1.7 }}>
            <div>Go Doorly, LLC</div>
            <div>NMLS #2764749</div>
            <div>Equal Housing Opportunity</div>
            <div>Doorly Realty, LLC | Real Broker, LLC</div>
            <a href="mailto:partners@godoorly.com" style={{ color: '#9ca3af', textDecoration: 'none', display: 'block', marginTop: 2 }}>partners@godoorly.com</a>
          </div>
        </div>
      </aside>
      )}

      {/* ── Main area ── */}
      <div style={{ marginLeft: isMobile ? 0 : SIDEBAR_WIDTH, flex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh', minWidth: 0, overflowX: 'hidden' }}>

        {/* Topbar */}
        <header style={{
          height: TOPBAR_HEIGHT, backgroundColor: isMobile ? PRIMARY : '#ffffff',
          borderBottom: isMobile ? 'none' : '1px solid #e5e7eb',
          position: 'sticky', top: 0, zIndex: 90, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: isMobile ? '0 16px' : '0 24px',
        }}>
          {isMobile ? (
            <>
              <Link to="/doorlypro/dashboard" style={{ display: 'flex', alignItems: 'center' }}>
                {/* 🔑 Phone top bar: 26px, unchanged — the larger logo is DESKTOP ONLY (Hannah 2026-11-03). */}
                <img src={`${process.env.PUBLIC_URL}/assets/DoorlyPro.png`} alt="DoorlyPro" data-testid="pro-logo-topbar"
                  style={{ height: 26, width: 'auto', filter: 'brightness(0) invert(1)' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              </Link>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button onClick={() => navigate('/doorlypro/alerts')} aria-label="Alerts"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', position: 'relative' }}>
                  <Icon name="bell" size={22} color="#fff" />
                  {unreadAlerts > 0 && (
                    <span style={{ position: 'absolute', top: 4, right: 4, background: '#ff082f', color: '#fff', fontSize: 9, fontWeight: 800, width: 16, height: 16, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #3a6ad9' }}>
                      {unreadAlerts > 9 ? '9+' : unreadAlerts}
                    </span>
                  )}
                </button>
                <button onClick={() => setMenuOpen(true)} aria-label="Open menu"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div style={{ width: '22px', height: '2px', background: '#fff', borderRadius: '2px' }} />
                  <div style={{ width: '22px', height: '2px', background: '#fff', borderRadius: '2px' }} />
                  <div style={{ width: '22px', height: '2px', background: '#fff', borderRadius: '2px' }} />
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                Welcome, <span style={{ fontWeight: 600, color: '#111827' }}>{partnerName}</span>
              </div>
              <button onClick={partnerLogout}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid #e5e7eb', backgroundColor: 'transparent', color: '#374151', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <Icon name="logout" size={15} /> Log Out
              </button>
            </>
          )}
        </header>

        {/* Page content */}
        <main style={{ flex: 1, backgroundColor: '#f9fafb', padding: isMobile ? '16px 14px 24px' : '28px 28px', boxSizing: 'border-box', overflowX: 'hidden', maxWidth: '100%' }}>
          {children}
          {/* ⚠️ Eva knows the audience from userType="partner" — the backend
              serves a different knowledge bundle and a different set of tools
              for partners, so this is not the borrower assistant with a
              different token. */}
          {partnerToken && <DoorlyChat token={partnerToken} userType="partner" />}
        </main>
      </div>

      {/* ── Mobile Nav Drawer ── */}
      {isMobile && menuOpen && (
      <>
        <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300 }} />
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '280px', background: '#fff', zIndex: 301, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 20px rgba(0,0,0,0.15)' }}>
          <div style={{ background: PRIMARY, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '800', color: '#fff' }}>{partnerName}</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
                {isHomebuilder ? 'Homebuilder' : partner?.partner_type === 'loan_officer' ? 'Loan Officer' : 'Realtor'}
              </div>
            </div>
            <button onClick={() => setMenuOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: '22px', lineHeight: 1, padding: '4px' }}>✕</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
            {visibleNav.map(item => {
              const isActive = item.path === '/doorlypro/referrals' ? location.pathname === '/doorlypro/referrals' : location.pathname === item.path;
              return (
                <button key={item.path} onClick={() => { navigate(item.path); setMenuOpen(false); }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', padding: '13px 20px', background: isActive ? '#eff6ff' : 'none', border: 'none', borderLeft: `3px solid ${isActive ? PRIMARY : 'transparent'}`, cursor: 'pointer', textAlign: 'left' }}>
                  <Icon name={item.mobileIcon} size={18} color={isActive ? PRIMARY : '#6b7280'} />
                  <span style={{ fontSize: '15px', fontWeight: isActive ? '700' : '500', color: isActive ? PRIMARY : '#374151', flex: 1 }}>{item.label}</span>
                  {navBadge(item)}
                </button>
              );
            })}
          </div>
          <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6' }}>
            <button onClick={() => { partnerLogout(); setMenuOpen(false); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', background: '#fff0f3', border: '1px solid #fecdd3', borderRadius: '10px', cursor: 'pointer', color: '#ff082f', fontWeight: '600', fontSize: '14px' }}>
              <Icon name="logout" size={18} color="#ff082f" /> Sign Out
            </button>
            <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '10px', color: '#d1d5db' }}>Go Doorly, LLC | NMLS #2764749</div>
          </div>
        </div>
      </>
      )}
    </div>
  );
}
